import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import ContractPreview from '../components/ContractPreview';
import { formatDateDMY } from '../utils/dateUtils';
import Button from '../components/UI/Button';
import FullScreenPanel from '../components/UI/FullScreenPanel';
import ResponsiveTable from '../components/ResponsiveTable';
import StatusBadge from '../components/UI/StatusBadge';
import { useTableSort } from '../components/UI/tableSort';
import KpiCard from '../components/Admin/KpiCard';
import { loansMetrics, owesBalance, toCurrency } from '../utils/adminMetrics';
import { exportToCsv } from '../utils/adminExport';
import { AuthContext } from '../context/AuthContext';
import { auditModalView } from '../utils/auditClient';
import InvoiceModal from '../components/InvoiceModal';
import { enrichLoanForInvoice } from '../utils/invoiceUtils';
import ClientLink from '../components/ClientLink';
import LoanLink from '../components/LoanLink';
import Pager from '../components/UI/Pager';

// Component to handle loan contract management
const ManageLoanContracts = () => {
  const { user } = useContext(AuthContext);
  const hasPerm = (p) => Array.isArray(user?.permissions) && user.permissions.includes(p);
  const canManageLoans = hasPerm('ManageLoans') || hasPerm('ManageConfig');
  // Treat a user with SignDocuments and without ManageLoans as a client-only viewer
  const isClient = Array.isArray(user?.permissions) && user.permissions.includes('SignDocuments') && !user.permissions.includes('ManageLoans');
  const [signedLoans, setSignedLoans] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [isGeneratingContract, setIsGeneratingContract] = useState(false);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // 'All' | 'Approved' | 'Overdue' | 'Owing' — set by the KPI tiles.
  const [tileFilter, setTileFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceLoan, setInvoiceLoan] = useState(null);
  
  const PAGE_SIZE = 10;

  useEffect(() => {
    fetchLoans();
  }, []);


  const fetchLoans = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
  const response = await axios.get(apiUrl('/api/loans'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allLoans = response.data || [];
      // Filter to only loans that have a fully generated/signed contract
      const onlySigned = await filterSignedLoans(allLoans);
      setSignedLoans(onlySigned);
    } catch (err) {
      console.error('Error fetching loans:', err);
      setError('Failed to fetch loans. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if contract exists
  const checkContractExists = async (loanId) => {
    try {
      const token = localStorage.getItem('token');
      // Make a HEAD request to check if contract exists
  const response = await axios.head(apiUrl(`/api/contracts/${loanId}/view`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.status === 200;
    } catch (err) {
      return false;
    }
  };

  // Build a signed-only list by checking contract existence for each loan
  const filterSignedLoans = async (list) => {
    if (!Array.isArray(list) || list.length === 0) return [];
    const results = await Promise.all(list.map(async (loan) => {
      const exists = await checkContractExists(loan.id);
      return exists ? loan : null;
    }));
    return results.filter(Boolean);
  };
  
  const handleViewContract = async (loan) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      
      // First, check if a contract exists for this loan
      const contractExists = await checkContractExists(loan.id);
      
      if (contractExists) {
        // If contract exists, open it directly
  try {
    const resp = await fetch(apiUrl(`/api/contracts/${loan.id}/view`), { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) return alert('Failed to open contract');
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  } catch { alert('Failed to open contract'); }
        setLoading(false);
        return;
      }
      
      // If no contract exists, fetch client data for the contract modal to generate one
  const clientResponse = await axios.get(apiUrl(`/api/clients/${loan.client_id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedLoan(loan);
      setClientData(clientResponse.data);
      setShowContractModal(true);
      auditModalView('Contract Modal');
    } catch (err) {
      console.error('Error fetching contract details:', err);
      setError('Failed to load contract preview. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateContract = async (updatedClientData) => {
    setIsGeneratingContract(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data for contract generation
      const contractData = {
        loanId: selectedLoan.id,
        clientId: selectedLoan.client_id,
        clientData: updatedClientData,
        loanData: selectedLoan
      };
      
      // Call API to generate contract
      const response = await axios.post(
  apiUrl('/api/contracts/generate'),
        contractData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage('Contract generated successfully!');
      
      // Optional: Refresh loan data after contract generation
      fetchLoans();
      
      // Close modal after success
      setTimeout(() => {
        setShowContractModal(false);
        setMessage('');
      }, 2000);
      
    } catch (err) {
      console.error('Error generating contract:', err);
      setMessage('Failed to generate contract: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsGeneratingContract(false);
    }
  };

  const handleBackToLoanForm = () => {
    setShowContractModal(false);
  };

  const canViewInvoice = (status) => !['Awaiting Client', 'Awaiting Staff', 'Awaiting Approval', 'Pending', 'Cancelled', 'Terminated', 'Rejected'].includes(status);

  const openInvoice = async (loan) => {
    try {
      const enriched = await enrichLoanForInvoice(loan);
      setInvoiceLoan(enriched);
      setShowInvoiceModal(true);
    } catch {
      setInvoiceLoan(loan);
      setShowInvoiceModal(true);
    }
  };

  // Filter loans based on search term
  const filteredLoans = signedLoans.filter(loan => {
    const fullName = (loan.clientName && String(loan.clientName).trim())
      || (`${loan.first_name || ''} ${loan.last_name || ''}`.trim())
      || (loan.client_name || '');
    const searchable = [
      loan.id?.toString?.() || '',
      loan.status || '',
      loan.principal_amount?.toString?.() || '',
      loan.start_date || '',
      loan.due_date || '',
      fullName
    ].join(' ').toLowerCase();

    if (!searchable.includes((searchTerm || '').toLowerCase())) return false;
    // A tile click narrows this same list to the loans that tile counts.
    if (tileFilter === 'Approved') return Boolean(loan.state?.approved);
    if (tileFilter === 'Overdue') return Boolean(loan.state?.is_overdue);
    if (tileFilter === 'Owing') return owesBalance(loan);
    return true;
  });

  // Paginate loans
  const totalPages = Math.ceil(filteredLoans.length / PAGE_SIZE);
  // A click on a column sorts every loan, then the page is taken.
  const loanSort = useTableSort(filteredLoans);
  const paginatedLoans = loanSort.rows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div className="max-w-full">

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-2 mt-2">
        {(() => {
          // The tiles describe the signed-contract list below (signedLoans),
          // and a click narrows that list to exactly the loans counted.
          const m = loansMetrics(signedLoans);
          const pick = (f) => { setTileFilter(prev => (prev === f ? 'All' : f)); setCurrentPage(1); };
          return (
          <>
            <KpiCard label="Signed Contracts" value={signedLoans.length} color="gray"
              onClick={() => pick('All')} title="Show every signed contract" />
            <KpiCard label="Approved Loans" value={m.approvedCount} color="blue"
              subtitle="Paid out, whatever since"
              onClick={() => pick('Approved')} title="Show the signed contracts of loans that were paid out" />
            <KpiCard label="Overdue Loans" value={m.overdueCount} color={m.overdueCount>0?'red':'green'}
              onClick={() => pick('Overdue')} title="Show the signed contracts of overdue loans" />
            <KpiCard label="Outstanding" value={toCurrency(m.outstandingAmount)} color="gray"
              subtitle={`${m.owingCount} loan${m.owingCount === 1 ? '' : 's'} with a balance`}
              onClick={() => pick('Owing')} title="Show the signed contracts with a balance owing" />
          </>
        ); })()}
      </div>

      {/* Search + Pagination row (search left, pagination right) */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-center gap-2">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search loans..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="p-2 w-full sm:w-[420px]"
          />
          <Button
            variant="primary"
            onClick={fetchLoans}
            className="min-w-[120px]"
            busyLabel="Refreshing…"
          >
            Refresh
          </Button>
        </div>

  <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <Pager page={currentPage} pages={totalPages} onPage={setCurrentPage} />
        </div>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      
  {/* Loans Table */}
      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
          <span className="ml-3 text-lg text-blue-700">Loading loans...</span>
        </div>
      ) : (
        <>
          <div className="p-2 rounded-lg bg-white dark:bg-slate-900 shadow-md">
            <div className="rounded-lg">
              <div className="p-2 flex justify-end">
                {canManageLoans && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const rows = filteredLoans.map(l => {
                        const base = {
                          LoanCode: l.loan_code || l.loanCode || '',
                          ClientCode: l.client_code || l.clientCode || '',
                          Status: l.status,
                          Principal: l.principal_amount,
                          StartDate: l.start_date ? formatDateDMY(l.start_date) : '',
                          DueDate: l.due_date ? formatDateDMY(l.due_date) : ''
                        };
                        if (!isClient) {
                          base.ClientName = (l.clientName && String(l.clientName).trim()) || (`${l.first_name || ''} ${l.last_name || ''}`.trim()) || (l.client_name || '');
                        }
                        return base;
                      });
                      exportToCsv('loan_contracts.csv', rows);
                    }}
                  >
                    Export CSV
                  </Button>
                )}
              </div>
              <ResponsiveTable
                data={paginatedLoans}
                sort={loanSort.sort}
                onSortChange={loanSort.onSortChange}
                columns={[
                  { field: 'loan_code', header: 'Loan Code', formatter: (_, loan) => (
                      <LoanLink loan={loan} className="font-medium">{loan.loan_code || loan.loanCode || '-'}</LoanLink>
                    ) },
                  { field: 'client_code', header: 'Client', formatter: (_, loan) => (
                      <ClientLink clientId={loan.client_id ?? loan.clientId} name={loan.client_code || loan.clientCode || '-'} />
                    ) },
                  ...(!isClient ? [
                    { field: 'client_name', header: 'Client Name', formatter: (_, loan) => {
                        const name = (loan.clientName && String(loan.clientName).trim())
                          || (`${loan.first_name || ''} ${loan.last_name || ''}`.trim())
                          || (loan.client_name || '');
                        return <ClientLink clientId={loan.client_id ?? loan.clientId} name={name || '-'} />;
                      } }
                  ] : []),
                  { field: 'principal_amount', header: 'Amount', formatter: (value) => (
                      <span className="text-gray-500 dark:text-slate-400">{toCurrency(parseFloat(value))}</span>
                    ) },
                  { field: 'status', header: 'Status', formatter: (value) => (
                      <StatusBadge status={value} />
                    ) },
                  { field: 'start_date', header: 'Start Date', formatter: (value) => (
                      <span className="text-gray-500 dark:text-slate-400">{formatDateDMY(value)}</span>
                    ) },
                  { field: 'due_date', header: 'Due Date', formatter: (value) => (
                      <span className="text-gray-500 dark:text-slate-400">{formatDateDMY(value)}</span>
                    ) },
                ]}
                actions={[
                  {
                    label: 'View Contract',
                    onClick: (loan) => handleViewContract(loan),
                    condition: () => canManageLoans,
                    tooltip: 'View or Generate Contract',
                  },
                  {
                    label: 'View Invoice',
                    onClick: (loan) => openInvoice(loan),
                    condition: (loan) => canViewInvoice(loan.status),
                    tooltip: 'View Invoice',
                  },
                ]}
                emptyMessage="No loans found matching your search criteria"
              />
            </div>
          </div>
        </>
      )}
      
      {/* Contract Modal */}
      {showContractModal && selectedLoan && clientData && (
        <FullScreenPanel
          isOpen
          onClose={() => { setShowContractModal(false); }}
          title="Review Loan Agreement"
          subtitle={[selectedLoan?.application_type, clientData?.personal_info?.first_name && `${clientData.personal_info.first_name} ${clientData.personal_info.last_name || ''}`.trim()].filter(Boolean).join(' · ')}
        >
              {/* Show error/success message */}
              {message && (
                <div className={`mb-8 p-4 rounded-lg border flex items-center justify-between ${
                  message.includes('Failed') || message.includes('Error') || message.includes('error')
                    ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700/50'
                    : 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700/50'
                }`}>
                  <span>{message}</span>
                  <button
                    onClick={() => setMessage('')}
                    className="ml-4 px-2 py-1 rounded-full text-lg font-bold hover:bg-gray-200 dark:hover:bg-slate-700 focus:outline-none"
                    aria-label="Close"
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              )}
              
              {/* Always show ContractPreview */}
              <ContractPreview
                loanData={{ ...selectedLoan, id: selectedLoan.id }}
                clientData={{
                  id: clientData.personal_info.id,
                  first_name: clientData.personal_info.first_name,
                  middle_name: clientData.personal_info.middle_name,
                  last_name: clientData.personal_info.last_name,
                  id_passport_no: clientData.personal_info.id_passport_no,
                  email: clientData.personal_info.email,
                  phone_number: clientData.personal_info.phone_number,
                  postal_address: clientData.personal_info.postal_address,
                  residential_address: clientData.personal_info.residential_address,
                  marital_status: clientData.personal_info.marital_status,

                  // Occupation details
                  occupation: clientData.occupation_info.occupation,
                  employer_name: clientData.occupation_info.employer_name,
                  payslip_number: clientData.occupation_info.payslip_number,
                  employer_address: clientData.occupation_info.employer_address,
                  employer_phone: clientData.occupation_info.employer_phone,

                  // Bank details
                  bank_name: clientData.bank_info.bank_name,
                  account_number: clientData.bank_info.account_number,
                  branch_code: clientData.bank_info.branch_code,
                  account_type: clientData.bank_info.account_type
                }}
                onEdit={handleBackToLoanForm}
                onGenerate={handleGenerateContract}
                loading={isGeneratingContract}
              />
        </FullScreenPanel>
      )}

      {showInvoiceModal && invoiceLoan && (
        <InvoiceModal
          visible={showInvoiceModal}
          loan={invoiceLoan}
          onClose={() => { setShowInvoiceModal(false); setInvoiceLoan(null); }}
        />
      )}
    </div>
  );
};

export default ManageLoanContracts;
